import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { formatDistance } from 'date-fns'; // Import formatDistance for relative time
import { FaMapMarkerAlt, FaArrowLeft, FaHeart, FaRegHeart, FaComment, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import supabase from "../../services/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { likePost, unlikePost } from "../../services/postService"; // Import like services
import { getComments, addComment, deleteComment } from "../../services/commentService"; // Import comment services
import CommentList from "../../components/feed/CommentList"; // Import CommentList component
import PostWithMap from "../../components/feed/PostWithMap"; // Import PostWithMap
import "./PostDetail.css";

export default function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const { user } = useAuth();
  
  const passedPostData = location.state?.postData;

  // Use passed data for fast initial render, but always fetch latest from DB
  useEffect(() => {
    let didCancel = false;
    const fetchPostData = async () => {
      setLoading(true);
      setError(null);
      try {
        // If we have passed data, show it immediately (optimistic UI)
        if (passedPostData && !post) {
          setPost(passedPostData);
        }
        // Always fetch latest from DB
        const { data: postDataFromDB, error: postError } = await supabase
          .from("posts")
          .select('*')
          .eq("id", postId)
          .single();
        if (postError) throw postError;
        if (!postDataFromDB) throw new Error("Post not found");
        
        // Fetch user data separately
        const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('user_id, username, profilePicture')
          .eq('user_id', postDataFromDB.user_id)
          .single();

        // Fetch post media separately
        const { data: postMedia, error: mediaError } = await supabase
          .from('post_media')
          .select('id, url, display_order')
          .eq('post_id', postId)
          .order('display_order');

        // Fetch post locations separately
        const { data: postLocations, error: locationError } = await supabase
          .from('post_locations')
          .select('name, lat, lng')
          .eq('post_id', postId);

        // Fetch likes separately
        const { data: likes, error: likesError } = await supabase
          .from('post_likes')
          .select('user_id, created_at')
          .eq('post_id', postId);
        
        // Process the post data to flatten structure for backward compatibility
        const processedPost = {
          ...postDataFromDB,
          // Add user data
          user: userError ? null : userData,
          // Extract place from post_locations (now stored directly)
          place: locationError || !postLocations || postLocations.length === 0 
            ? null 
            : {
                id: postLocations[0].id,
                name: postLocations[0].name,
                lat: postLocations[0].lat,
                lng: postLocations[0].lng
              },
          place_id: locationError || !postLocations || postLocations.length === 0 
            ? null 
            : postLocations[0].id,
          // Extract images from post_media and rename to post_images for compatibility
          post_images: mediaError || !postMedia 
            ? [] 
            : postMedia.sort((a, b) => a.display_order - b.display_order),
          // Set primary image_url for backward compatibility
          image_url: mediaError || !postMedia || postMedia.length === 0
            ? null
            : postMedia.sort((a, b) => a.display_order - b.display_order)[0].url,
          // Add likes and calculate likes_count
          likes: likesError ? [] : likes,
          likes_count: likesError ? 0 : (likes ? likes.length : 0)
        };

        // Fetch comment count in a separate query
        const { count: commentCount, error: countError } = await supabase
          .from('post_comments')
          .select('*', { count: 'exact', head: true })
          .eq('post_id', postId);
        if (countError) throw countError;
        processedPost.comments_count = commentCount || 0;

        // Remove separate likes fetch
        // Fetch user if not joined
        if (processedPost.user_id && (!processedPost.user || !processedPost.user.username)) {
          const { data: userData, error: userError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', processedPost.user_id)
            .single();
          if (!userError && userData) {
            processedPost.user = userData;
          }
        }
        // Fetch place if not joined
        if (processedPost.place_id && (!processedPost.place || !processedPost.place.lat || !processedPost.place.lng)) {
          const { data: placeData, error: placeError } = await supabase
            .from('places')
            .select('*')
            .eq('id', processedPost.place_id)
            .single();
          if (!placeError && placeData) {
            processedPost.place = placeData;
          }
        }
        if (!didCancel) {
          setPost(processedPost);
          setIsLiked(processedPost.likes?.some(like => like.user_id === user?.id) || false);
        }
      } catch (err) {
        if (!didCancel) {
          setError(err.message || "Failed to load post");
        }
      } finally {
        if (!didCancel) setLoading(false);
      }
    };
    if (postId) {
      fetchPostData();
    }
    return () => { didCancel = true; };
  }, [postId, user]);
  
  // Fetch comments for the post
  useEffect(() => {
    const fetchComments = async () => {
      if (!postId) return;
      setLoadingComments(true);
      setCommentsLoaded(false);
      try {
        const fetchedComments = await getComments(postId);
        setComments(fetchedComments || []);
        setPost(prev => prev ? { ...prev, comments_count: (fetchedComments?.length || 0) } : prev);
      } catch (err) {
        setComments([]);
        setPost(prev => prev ? { ...prev, comments_count: 0 } : prev);
      } finally {
        setLoadingComments(false);
        setCommentsLoaded(true);
      }
    };
    fetchComments();
  }, [postId]);

  // Add a handlePostUpdate function like in Feed.js
  const handlePostUpdate = (updatedPost) => {
    setPost(prevPost => ({
      ...prevPost,
      ...updatedPost,
    }));
  };

  // Handler for adding a comment
  const handleCommentAdded = async (newComment) => {
    setComments(prev => {
      const updated = [...prev, newComment];
      // Create a new post object with updated comments_count
      handlePostUpdate({ ...post, comments_count: updated.length });
      return updated;
    });
  };

  // Handler for deleting a comment
  const handleCommentDeleted = (commentId) => {
    setComments(prev => {
      const updated = prev.filter(c => c.id !== commentId);
      handlePostUpdate({ ...post, comments_count: updated.length });
      return updated;
    });
  };

  // Handler for like/unlike: re-fetch likes from backend
  const handleLikeToggle = async () => {
    console.log('[PostDetail] handleLikeToggle called');
    if (!user || !post) return;
    setIsLiking(true);
    const isLikedNow = post.likes?.some(like => like.user_id === user.id);
    // Optimistic update
    const prevLikes = post.likes || [];
    const updatedLikes = isLikedNow
      ? prevLikes.filter(like => like.user_id !== user.id)
      : [...prevLikes, { user_id: user.id, created_at: new Date().toISOString() }];
    
    // Calculate new likes count
    const newLikesCount = updatedLikes.length;
    
    setPost(prev => prev ? { 
      ...prev, 
      likes: updatedLikes,
      likes_count: newLikesCount 
    } : prev);
    setIsLiked(!isLikedNow);
    try {
      if (isLikedNow) {
        await unlikePost(post.id);
      } else {
        await likePost(post.id);
      }
      // Success: do nothing, UI already updated
    } catch (err) {
      // Revert optimistic update on failure
      setPost(prev => prev ? { 
        ...prev, 
        likes: prevLikes,
        likes_count: prevLikes.length 
      } : prev);
      setIsLiked(isLikedNow);
      // Optionally show error
    } finally {
      setIsLiking(false);
      console.log('[PostDetail] isLiking:', false);
    }
  };

  // Handler for when PostWithMap updates the post (e.g., like, comment count)
  const handlePostInternalUpdate = (updatedPost) => {
    setPost(prevPost => {
      const newPost = {
        ...prevPost,
        ...updatedPost,
        comments_count: updatedPost.comments_count !== undefined ? updatedPost.comments_count : prevPost.comments_count,
        likes: updatedPost.likes !== undefined ? updatedPost.likes : prevPost.likes,
      };
      console.log('[PostDetail] handlePostInternalUpdate:', { updatedPost, newPost });
      return newPost;
    });
  };

  // Handler for when PostWithMap deletes the post
  const handlePostInternalDelete = (deletedPostId) => {
    console.log(`Post ${deletedPostId} deleted from PostWithMap, navigating back.`);
    navigate('/feed'); // Or to a more appropriate page
  };
  
  // Handler for location click from PostWithMap (optional, can be a no-op)
  const handleLocationInternalClick = (locationData) => {
    console.log("Location clicked in PostWithMap:", locationData);
    // Optionally, could focus a map if PostDetail also had one, or do nothing.
  };
  
  // Handler to go back to previous page
  const handleGoBack = () => {
    if (location.state?.previousPath) {
      navigate(location.state.previousPath);
    } else {
      navigate(-1);
    }
  };

  // Ensure post.likes is always an array
  const safePost = post ? { ...post, likes: Array.isArray(post.likes) ? post.likes : [] } : post;

  // In the render, only show PostWithMap when both post and comments are loaded
  if (loading || !commentsLoaded) {
    return <div className="post-detail-container loading">Loading post...</div>;
  }
  
  if (error) {
    return (
      <div className="post-detail-container error">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="back-button" onClick={handleGoBack}>
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="post-detail-container not-found">
        <div className="not-found-content">
          <h2>Post Not Found</h2>
          <p>The post you're looking for doesn't exist or has been removed.</p>
          <button className="back-button" onClick={handleGoBack}>
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // Log the post object to inspect its structure, especially post.place and post.post_images
  if (post) {
    console.log("[PostDetail] Post data being passed to PostWithMap:", JSON.stringify(post, null, 2));
    console.log("[PostDetail] Post place data:", JSON.stringify(post.place, null, 2));
    console.log("[PostDetail] Post images data:", JSON.stringify(post.post_images, null, 2));
  }

  return (
    <div className="post-detail-container">
      <div className="back-navigation">
        <button className="back-button" onClick={handleGoBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      
      <div className="post-detail-content-wrapper">
        <PostWithMap 
            post={safePost} 
          onPostUpdate={handlePostUpdate}
          onPostDelete={handlePostInternalDelete}
          onLocationClick={handleLocationInternalClick}
          comments={comments}
          loadingComments={loadingComments}
          onCommentAdded={handleCommentAdded}
          onCommentDeleted={handleCommentDeleted}
          onLikeToggle={handleLikeToggle}
          isLiked={isLiked}
          isLiking={isLiking}
        />
      </div>
    </div>
  );
} 